import React, { useEffect, useState } from 'react';
import {
  Title,
  Button,
  Icon,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Grid,
  Col,
} from '@common/components';
import PropTypes from 'prop-types';
import {
  faCircleExclamation,
  faInfo,
  faQuestionCircle,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { setErrorClose } from '@app/slices/modals';
import { useAppDispatch } from '@app/hooks';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import alertClasses from './AlertModal.module.scss';

export default function AlertModal({
  type = 'info',
  className,
  title,
  text,
  show = false,
  onAccept,
  onCancel,
  onClose,
}) {
  const { t } = useTranslation('app\\AlertModal');

  const [visible, setVisible] = useState(show || false);
  const dispatch = useAppDispatch();
  const classNames = cx(className, alertClasses.modal);

  const icons = {
    done: faCheckCircle,
    info: faInfo,
    question: faQuestionCircle,
    error: faCircleExclamation,
  };

  const colors = {
    done: 'accent',
    info: 'accent',
    question: 'warning',
    error: 'error',
  };

  const icon = icons[type];
  const color = colors[type];

  useEffect(() => {
    setVisible(show || false);
  }, [show]);

  const handleAccept = () => {
    if (onAccept) onAccept();
    setVisible(false);
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    setVisible(false);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch(setErrorClose());
    }

    setVisible(false);
  };

  return (
    <Modal
      closeOnClickOutside
      onClose={handleClose}
      visible={visible}
      className={classNames}
    >
      <ModalHeader>
        <Title className={alertClasses.title}>
          <Grid className={alertClasses.grid} gutter={8}>
            <Col>
              <Icon icon={icon} size="sm" color={color} />
            </Col>
            <Col>{t(title)}</Col>
          </Grid>
        </Title>
      </ModalHeader>
      <ModalContent className={alertClasses.center}>
        <div>{t(text)}</div>
      </ModalContent>
      <ModalFooter className={alertClasses.center}>
        <Button variant="outline" color="accent" onClick={handleAccept}>
          {t('btnOkLabel')}
        </Button>
        {type === 'question' && onCancel ? (
          <Button variant="outline" color="error" onClick={handleCancel}>
            {t('btnCancelLabel')}
          </Button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
}

AlertModal.propTypes = {
  className: PropTypes.string,
  type: PropTypes.oneOf(['done', 'info', 'error', 'question']),
  title: PropTypes.string,
  text: PropTypes.string,
  show: PropTypes.bool,
  onAccept: PropTypes.func,
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
};
